import { Alignment, Color, DisplayType } from "@mcleod/core";
import { ClearButtonVisible } from "../textbox/ClearButtonVisible";
import { ComponentPropDefinitionUtil, ComponentPropDefinitions, ComponentProps } from "../../base/ComponentProps";
import { PropType } from "../../base/PropType";
import { TextboxItemsSource } from "../textbox/Textbox";

/**
 * UnitInputFieldProps are built from two main sources:
 * - ComponentProps (obviously)
 * - Select properties from TextboxProps are replicated here, although they often have a new name.  These are either:
 *     - Used by the overall component (example: caption).  These properties probably retain the same name as the
 *       property in Textbox.
 *     - Used by the number textbox (examples: maxValue, placeholder).  These properties have been renamed to indicate
 *       they are specific to the number textbox.
 *     - Used by the unit textbox (examples: allowDropdownBlank, items).  These properties have been renamed to indicate
 *       they are specific to the unit textbox.
 */
export interface UnitInputFieldProps extends ComponentProps {
    caption: string;
    captionAlignment: Alignment.LEFT | Alignment.TOP;
    captionVisible: boolean;
    captionVisibleInsideTable: boolean;
    displayType: DisplayType;

    // number* properties are passed through to the inner textbox that manages the number value (TextboxNumber)
    numberClearButtonVisible: ClearButtonVisible;
    numberMaxValue: number;
    numberMinValue: number;
    numberPlaceholder: string;
    numberPlaceholderColor: Color;
    numberText: string;

    printable: boolean;
    printableDuringAdd: boolean;
    printableDuringSearch: boolean;
    printableDuringUpdate: boolean;

    // unit* properties are passed through to the inner textbox that manages the unit (TextboxUnit)
    unitAllowDropdownBlank: boolean;
    unitAllowDropdownMultiSelect: boolean;
    unitDropdownWidth: number;
    unitItems: TextboxItemsSource;
    unitWidth: string|number;
}

export class UnitInputFieldPropDefinitions {
    private static unitInputFieldPropDefs: ComponentPropDefinitions;

    public static getDefinitions(): ComponentPropDefinitions {
        if (this.unitInputFieldPropDefs == null) {
            this.unitInputFieldPropDefs = {
                ...ComponentPropDefinitionUtil.getComponentPropertyDefinitions(),
                ...this.buildOverallProps(),
                ...this.buildNumberProps(),
                ...this.buildUnitProps(),
                ...this.buildEventProps(),
                ...this.buildPermissionsProps()
            };
            ComponentPropDefinitionUtil.populateComponentPropNames(this.unitInputFieldPropDefs);
            this.unitInputFieldPropDefs.align.defaultValue = Alignment.RIGHT;
            this.unitInputFieldPropDefs.width.defaultValue = "175";
            this.hideUnwantedComponentProps();
        }
        return this.unitInputFieldPropDefs;
    }

    /**
     * Defines properties that are specific to the overall UnitInputField
     */
    private static buildOverallProps(): ComponentPropDefinitions {
        return {
            caption: {
                type: PropType.string,
                affectsProps: ["id", "displayLabel"],
                visibleInMcLeodTailor: true,
                description: "This controls the label that appears near the input."
            },
            captionAlignment: {
                type: PropType.string,
                dropdownProps: { items: [Alignment.LEFT, Alignment.TOP] },
                defaultValue: Alignment.TOP,
                visibleInMcLeodTailor: true,
                description: "This controls where the caption is displayed, which can be either above the component, or to its left."
            },
            captionVisible: {
                type: PropType.bool,
                defaultValue: true,
                visibleInMcLeodTailor: true,
                description: "This toggles whether or not the caption will be shown.  If set to false, there will be no caption or additional whitespace where the caption would normally go."
            },
            captionVisibleInsideTable: {
                type: PropType.bool,
                defaultValue: false,
                visibleInMcLeodTailor: true,
                description: "This specifies if the caption should be displayed when the component is presented inside of a table row. "
            },
            displayType: {
                type: PropType.string, dropdownProps: {
                    items: Object.values(DisplayType)
                },
                category: "Data",
                visibleInMcLeodTailor: true,
                editableInMcLeodTailor: { baseComponent: false, customComponent: true },
                description: "The display type controls the display and validation of the component."
            },
            printable: {
                type: PropType.bool,
                defaultValue: false,
                category: "Appearance",
                visibleInMcLeodTailor: true,
                description: "This toggles whether the component appears as a caption with a user-editable input box or whether it should be displayed as a caption with a non-editable Label.  This is often changed at runtime.",
            },
            printableDuringAdd: {
                type: PropType.bool,
                defaultValue: false,
                category: "Appearance",
                visibleInMcLeodTailor: true,
                description: "This toggles whether the component should appear in its printable state during add mode.",
            },
            printableDuringSearch: {
                type: PropType.bool,
                defaultValue: false,
                category: "Appearance",
                visibleInMcLeodTailor: true,
                description: "This toggles whether the component should appear in its printable state during search mode.",
            },
            printableDuringUpdate: {
                type: PropType.bool,
                defaultValue: false,
                category: "Appearance",
                visibleInMcLeodTailor: true,
                description: "This toggles whether the component should appear in its printable state during update mode.",
            }
        };
    }

    /**
     * Defines properties that are specific to the inner textbox that manages the number value (TextboxNumber)
     */
    private static buildNumberProps(): ComponentPropDefinitions {
        const category = "Number";
        return {
            numberClearButtonVisible: {
                type: PropType.string,
                category,
                defaultValue: ClearButtonVisible.WHEN_FOCUSED,
                dropdownProps: { items: Object.values(ClearButtonVisible) },
                visibleInMcLeodTailor: true,
                description: "This specifies whether there is an 'X' button on the right side of the number input that will clear out the text."
            },
            numberMaxValue: {
                type: PropType.number,
                category,
                visibleInMcLeodTailor: true,
                description: "This specifies the maximum value that can be entered."
            },
            numberMinValue: {
                type: PropType.number,
                category,
                visibleInMcLeodTailor: true,
                description: "This specifies the minimum value that can be entered."
            },
            numberPlaceholder: {
                type: PropType.string,
                category,
                visibleInMcLeodTailor: true,
                description: "When set, placeholder text will appear in the number input when it is empty."
            },
            numberPlaceholderColor: {
                type: PropType.string,
                category,
                description: "Specifies the color of the placeholder text."
            },
            numberText: {
                type: PropType.string,
                category,
                visibleInMcLeodTailor: true,
                description: "This sets the value of the number input."
            }
        }
    }

    /**
     * Defines properties that are specific to the inner textbox that manages the unit (TextboxUnit)
     */
    private static buildUnitProps(): ComponentPropDefinitions {
        const category = "Unit";
        return {
            unitAllowDropdownBlank: {
                type: PropType.bool,
                category,
                defaultValue: false,
                visibleInMcLeodTailor: true,
                editableInMcLeodTailor: { baseComponent: false, customComponent: true },
                description: "Allows for control over the insertion of the '--' (blank) option in a dropdown.  If this property is 'true', the '--' option will be added to the dropdown if the field is not required.  If this property is 'false', the '--' option will not be added to the dropdown, regardless of whether or not the field is required.  The default value is 'true'."
            },
            unitAllowDropdownMultiSelect: {
                type: PropType.bool,
                category,
                description: "Enables the selection of multiple items from the unit dropdown. When set to true, users can select and save multiple values, which will be separated by the specified valueDelimiter. When searching, all selected values are included in the search filter.",
                visibleInDesigner: false,
                visibleInMcLeodTailor: false,
                defaultValue: false
            },
            unitDropdownWidth: {
                type: PropType.number,
                category,
                visibleInDesigner: false,
                visibleInMcLeodTailor: false,
                description: "This specifies the width of the dropdown that presents choices to the user.  The default value is set based on the type of value being displayed (distance, length, etc.).  As a result, this property will rarely be set/changed."
            },
            unitItems: {
                type: PropType.dropdownItem,
                category,
                visibleInMcLeodTailor: true,
                description: "This specifies the list of possible units.  The default list of items is set based on the type of value being displayed (distance, length, etc.).  As a result, this property will rarely be set/changed."
            },
            unitWidth: {
                type: PropType.number,
                category,
                defaultValue: "60",
                visibleInMcLeodTailor: true,
                description: "Specifies the width of the unit portion of the component.  This property should be rarely set/changed."
            }
        };
    }

    /**
     * Defines properties related to available event listeners
     */
    private static buildEventProps(): ComponentPropDefinitions {
        const category = "Events";
        return {
            numberOnBlur: {
                type: PropType.event,
                category,
                addListenerMethod: "addNumberBlurListener",
                removeListenerMethod: "removeNumberBlurListener",
                eventSignature: "NumberOnBlur(event: BlurEvent)",
                description: "This event will fire when the number portion of the component component is the current focus of the page and then a different component becomes focused (i.e. the user tabbed to another field)."
            },
            numberOnChange: {
                type: PropType.event,
                category,
                addListenerMethod: "addNumberChangeListener",
                removeListenerMethod: "removeNumberChangeListener",
                eventSignature: "NumberOnChange(event: ChangeEvent)",
                description: "This specifies a function to be called when the number value changes."
            },
            numberOnFocus: {
                type: PropType.event,
                category,
                addListenerMethod: "addNumberFocusListener",
                removeListenerMethod: "removeNumberFocusListener",
                eventSignature: "NumberOnFocus(event: FocusEvent)",
                description: "This event will fire when the number portion of the component becomes the current focus target of the page."
            },
            numberOnKeyDown: {
                type: PropType.event,
                category,
                addListenerMethod: "addNumberKeyDownListener",
                removeListenerMethod: "removeNumberKeyDownListener",
                eventSignature: "NumberOnKeyDown(event: KeyEvent)",
                description: "This event will fire when a key is pressed while updating the number value."
            },
            numberOnKeyUp: {
                type: PropType.event,
                category,
                addListenerMethod: "addNumberKeyUpListener",
                removeListenerMethod: "removeNumberKeyUpListener",
                eventSignature: "NumberOnKeyUp(event: KeyEvent)",
                description: "This event will fire when a key is released while updating the number value."
            },
            unitBeforeSelection: {
                type: PropType.event,
                category,
                addListenerMethod: "addUnitBeforeSelectionListener",
                removeListenerMethod: "removeUnitBeforeSelectionListener",
                eventSignature: "UnitBeforeSelection(event: DropdownSelectionEvent)",
                description: "This specifies a function to be called before the user selects a unit from the dropdown."
            },
            unitAfterSelection: {
                type: PropType.event,
                category,
                addListenerMethod: "addUnitAfterSelectionListener",
                removeListenerMethod: "removeUnitAfterSelectionListener",
                eventSignature: "UnitAfterSelection(event: DropdownSelectionEvent)",
                description: "This specifies a function to be called after the user selects a unit from the dropdown."
            },
            onPrintable: {
                type: PropType.event,
                category: "Events",
                addListenerMethod: "addPrintableListener",
                removeListenerMethod: "removePrintableListener",
                eventSignature: "OnPrintable(event: PrintableEvent)",
                description: "This specifies a function to be called when the component's printable property has changed."
            }
        };
    }

    /**
     * Defines properties related to permissions
     */
    private static buildPermissionsProps(): ComponentPropDefinitions {
        const category = "Permissions";
        return {
            numberDisabledByServer: {
                type: PropType.bool,
                category,
                defaultValue: false,
                visibleInMcLeodTailor: false,
                visibleInDesigner: false,
                description: "This property is set to true when the server has determined that the number value cannot be changed (due to permissions settings)."
            },
            unitDisabledByServer: {
                type: PropType.bool,
                category,
                defaultValue: false,
                visibleInMcLeodTailor: false,
                visibleInDesigner: false,
                description: "This property is set to true when the server has determined that the unit value cannot be changed (due to permissions settings)."
            }
        };
    }

    /**
     * Marks members of ComponentProps that we don't want to use to be hidden in the UI Designer/Tailor.
     */
    private static hideUnwantedComponentProps() {
        this.getComponentPropsToHide().forEach(propName => {
            const propDef = this.unitInputFieldPropDefs[propName];
            propDef.visibleInDesigner = false;
            propDef.visibleInMcLeodTailor = false;
        });
    }

    private static getComponentPropsToHide(): Set<string> {
        return new Set<string>()
            .add("cursor")
            .add("draggable")
            .add("excludeFromSortFields")
            .add("isRow")
            .add("multiCurrency")
            .add("onBlur")
            .add("onClick")
            .add("onDblClick")
            .add("onDragDrop")
            .add("onDragEnd")
            .add("onDragOver")
            .add("onDragStart")
            .add("onFocus")
            .add("onKeyDown")
            .add("onKeyUp")
            .add("rememberUserChoice");
    }
}
